import api from './api';
import type { Club, ClubMembership, ClubNews, MembershipPricing } from '../types/clubs';

/**
 * Club Service
 * Handles all API calls related to clubs for the web platform
 */

/**
 * Get all clubs (public discovery)
 */
export const getClubs = async (params?: {
  search?: string;
  city?: string;
}): Promise<Club[]> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.city) queryParams.append('city', params.city);
    
    const queryString = queryParams.toString();
    const url = `/clubs${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data.clubs || [];
  } catch (error: any) {
    // If clubs endpoint is not available or returns an error, return empty array
    // This allows the UI to show "no clubs available" instead of an error message
    console.warn('Failed to fetch clubs:', error?.response?.status || error);
    return [];
  }
};

/**
 * Get club details by ID
 */
export const getClub = async (id: string): Promise<Club> => {
  const response = await api.get(`/clubs/${id}`);
  return response.data.club;
};

/**
 * Get user's clubs (memberships)
 */
export const getMyClubs = async (): Promise<Club[]> => {
  const response = await api.get('/clubs/my-clubs');
  return response.data.clubs || [];
};

/**
 * Join a club
 */
export const joinClub = async (clubId: string): Promise<ClubMembership> => {
  const response = await api.post(`/clubs/${clubId}/join`);
  return response.data.membership;
};

/**
 * Send inquiry to club admin (when autoEnrollment is false)
 */
export const inquireClub = async (
  clubId: string,
  message: string
): Promise<ClubMembership> => {
  const response = await api.post(`/clubs/${clubId}/inquire`, {
    message,
  });
  return response.data.membership;
};

/**
 * Get club news
 */
export const getClubNews = async (clubId: string): Promise<ClubNews[]> => {
  const response = await api.get(`/clubs/${clubId}/news`);
  return response.data.news || [];
};

/**
 * Get membership pricing information
 */
export const getMembershipPricing = async (
  clubId: string
): Promise<MembershipPricing> => {
  const response = await api.get(`/clubs/${clubId}/memberships/pricing`);
  return response.data;
};

/**
 * Create membership checkout session
 */
export const createMembershipCheckout = async (
  clubId: string,
  period: 'monthly' | 'annual'
): Promise<{ sessionId: string; url: string }> => {
  const response = await api.post(`/clubs/${clubId}/memberships/join`, {
    period,
  });
  return {
    sessionId: response.data.sessionId,
    url: response.data.url,
  };
};

/**
 * Cancel membership
 */
export const cancelMembership = async (
  clubId: string,
  membershipId: string,
  immediately: boolean = false
): Promise<ClubMembership> => {
  const response = await api.post(
    `/clubs/${clubId}/memberships/${membershipId}/cancel`,
    { immediately }
  );
  return response.data.membership;
};

/**
 * Renew membership
 */
export const renewMembership = async (
  clubId: string,
  membershipId: string,
  period: 'monthly' | 'annual' = 'monthly'
): Promise<{ sessionId: string; url: string }> => {
  const response = await api.post(
    `/clubs/${clubId}/memberships/${membershipId}/renew`,
    { period }
  );
  return {
    sessionId: response.data.sessionId,
    url: response.data.url,
  };
};

/**
 * Club ride for upcoming rides display
 */
export interface ClubRide {
  id: string;
  name: string;
  departureDate: string;
  departureTime?: string;
  departureLocation: string;
  distanceKm: number;
  maxRiders: number;
  rideType?: string;
  city?: string;
  _count: {
    participants: number;
  };
}

/**
 * Get upcoming rides for a club (public, limited to 3)
 */
export const getClubUpcomingRides = async (clubId: string): Promise<ClubRide[]> => {
  try {
    const response = await api.get(`/clubs/${clubId}/upcoming-rides`);
    return response.data.rides || [];
  } catch (error: any) {
    console.warn('Failed to fetch club rides:', error?.response?.status || error);
    return [];
  }
};
